import React, { useState, useEffect } from 'react';
import './GetQuote.css';

import { Heading } from '../../../components';

import { FaUser, FaEnvelope, FaPhone, FaBuilding, FaCogs, FaChartBar, FaDollarSign, FaCalendar, FaComment, FaAngleDown } from 'react-icons/fa';

import axios from "axios";


// API endpoint for form submission
const API_PATH = 'https://technova-react.asdesignsgalaxy.com/api/phpmailer/quote.php';

// Regular expressions for email and phone number validation
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;



const GetQuote = () => {

  // State for form data with initial empty values
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    number: '',
    service: '',
    businessSize: '',
    budget: '',
    timeline: '',
    description: ''
  });

  // State for tracking form submission status
  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Effect to automatically hide success message after 3 seconds
  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  // Form submission handler
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Field validation
      const validationErrors = [];

      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.company.trim()) validationErrors.push('Company name is required');
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.service) validationErrors.push('Service is required');
      if (!formData.businessSize) validationErrors.push('Business size is required');
      if (!formData.budget) validationErrors.push('Budget is required');
      if (!formData.timeline) validationErrors.push('Timeline is required');
      if (!formData.description) validationErrors.push('Project description is required');

      // If validation errors exist, throw them
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      // Prepare form data for submission
      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      // Make API call to submit form data
      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Handle API response
      if (!response.data.sent) {
        throw new Error(response.data.message || 'Form submission failed');
      }

      // On success, reset form and show success message
      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        company: '',
        number: '',
        service: '',
        businessSize: '',
        budget: '',
        timeline: '',
        description: ''
      });
    } catch (err) {
      // Handle errors from API or validation
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      // Reset submission state
      setIsSubmitting(false);
    }
  };

  // Handler for input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section className="request-quote">
      <div className="box-container">
        <div className="content">
          <form className="dark-form" onSubmit={handleSubmitForm}>

            {/* Form Heading */}
            <Heading title={'Request a Quote'} subTitle={'Get a Quote'} linear />

            <div className="input-box">

              {/* Full Name Input Field */}
              <div className="input-field">
                <label htmlFor="name"><FaUser /></label>
                <input
                  type="text"
                  name="name"
                  className="box"
                  id="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Email Input Field */}
              <div className="input-field">
                <label htmlFor="email"><FaEnvelope /></label>
                <input
                  type="email"
                  name="email"
                  className="box"
                  id="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">

              {/* Company Name Input Field */}
              <div className="input-field">
                <label htmlFor="company"><FaBuilding /></label>
                <input
                  type="text"
                  name="company"
                  className="box"
                  id="company"
                  placeholder="Company Name"
                  value={formData.company}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Phone Number Input Field */}
              <div className="input-field">
                <label htmlFor="number"><FaPhone /></label>
                <input
                  type="tel"
                  name="number"
                  className="box"
                  id="number"
                  placeholder="Phone Number"
                  value={formData.number}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

            </div>

            <div className="input-box">

              {/* Service Required Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="service"><FaCogs /></label>
                  <select
                    className="select-box"
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Service Required</option>
                    <option value="Data Insights">Data Insights</option>
                    <option value="Machine Learning">Machine Learning</option>
                    <option value="Cloud Automation">Cloud Automation</option>
                    <option value="AI Integration">AI Integration</option>
                    <option value="Predictive Analytics">Predictive Analytics</option>
                  </select>
                  <div className="icon-container">
                    <FaAngleDown className="icon" />
                  </div>
                </div>
              </div>

              {/* Business Size Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="businessSize"><FaChartBar /></label>
                  <select
                    className="select-box"
                    id="businessSize"
                    name="businessSize"
                    value={formData.businessSize}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Business Size</option>
                    <option value="Small">Small</option>
                    <option value="Medium">Medium</option>
                    <option value="Large">Large</option>
                  </select>
                  <div className="icon-container">
                    <FaAngleDown className="icon" />
                  </div>
                </div>
              </div>

            </div>

            <div className="input-box">

              {/* Estimated Budget Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="budget"><FaDollarSign /></label>
                  <select
                    className="select-box"
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Estimated Budget</option>
                    <option value="Under-5k">Under $5,000</option>
                    <option value="5k-10k">$5,000 - $10,000</option>
                    <option value="10k-25k">$10,000 - $25,000</option>
                    <option value="25k-50k">$25,000 - $50,000</option>
                    <option value="50k-Plus">$50,000+</option>
                  </select>
                  <div className="icon-container">
                    <FaAngleDown className="icon" />
                  </div>
                </div>
              </div>

              {/* Project Timeline Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="timeline"><FaCalendar /></label>
                  <select
                    className="select-box"
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Project Timeline</option>
                    <option value="Less Than 1 Month">Less than 1 month</option>
                    <option value="1-3 Months">1-3 months</option>
                    <option value="3-6 Months">3-6 months</option>
                    <option value="6-12 Months">6-12 months</option>
                    <option value="More than 1 Year">More than 1 year</option>
                  </select>
                  <div className="icon-container">
                    <FaAngleDown className="icon" />
                  </div>
                </div>
              </div>

            </div>

            {/* Project Description Text Area */}
            <div className="input-field text-area">
              <label htmlFor="description"><FaComment /></label>
              <textarea
                cols="30"
                rows="10"
                name="description"
                className="box"
                id="description"
                placeholder="Project Description"
                value={formData.description}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              ></textarea>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="btn" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Get Quote'}
            </button>

            {/* Error and Success Messages */}
            {error && (
              <span className="alert error" id="msg">
                {error.split(' | ').map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
              </span>
            )}
            {mailSent && <span className="alert success" id="msg"><span>Request Submitted Successfully</span></span>}
          </form>
        </div>
      </div>
    </section>
  );
}

export default GetQuote;